import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { getFlow, progress, questionKind, resume } from '@tutlo/core'
import { Button, Input, Option, Progress } from '@tutlo/ui'
import { useEffect, useRef, useState } from 'react'
import { AskFooter, Shell } from '../layout.tsx'
import { ApiError, useAnswer, useCreateLead, useLead } from '../lead.ts'
import { QuestionIllustration } from '../screens/quiz/illustration.tsx'
import { utmFromSearch, validateSearch } from '../search.ts'

export const Route = createFileRoute('/q/$slug')({
  validateSearch,
  loader: ({ params, location }) => {
    const flow = getFlow(params.slug)
    // A mistyped slug in a campaign link must not cost the click: fall back to the default
    // qualifier and keep the ad params.
    if (!flow) {
      throw redirect({
        to: '/q/$slug',
        params: { slug: 'default' },
        search: validateSearch(location.search as Record<string, unknown>),
        replace: true,
      })
    }
    return flow
  },
  component: QuizRoute,
})

/** Wejście bez leada (np. `/q/test` albo wygasłe cookie) → tworzymy leada i dopiero renderujemy pytania. */
function QuizRoute() {
  const flow = Route.useLoaderData()
  const search = Route.useSearch()
  const lead = useLead()
  const createLead = useCreateLead()

  const needsLead = lead.isSuccess && (!lead.data || lead.data.flowSlug !== flow.slug)
  useEffect(() => {
    if (needsLead && createLead.isIdle) {
      createLead.mutate({
        flowSlug: flow.slug,
        ...(search.v ? { variant: search.v } : {}),
        utm: utmFromSearch(search),
        landingUrl: window.location.href.slice(0, 2000),
      })
    }
  }, [needsLead, createLead, flow.slug, search])

  if (lead.isPending || needsLead) {
    // A failed create (flaky mobile network, 5xx) must not strand a paid click on "Chwila…":
    // `reset()` returns the mutation to idle, so the effect above fires it again.
    return (
      <Shell nav={{}}>
        {createLead.isError ? (
          <div className="flex flex-col gap-base pt-xl">
            <p className="text-center text-body text-error" role="alert">
              Nie udało się zacząć. Sprawdź połączenie i spróbuj jeszcze raz.
            </p>
            <Button onClick={() => createLead.reset()}>Spróbuj ponownie</Button>
          </div>
        ) : (
          <p className="pt-xl text-center text-body text-text-secondary" aria-live="polite">
            Chwila…
          </p>
        )}
      </Shell>
    )
  }
  if (!lead.data) return null
  return <QuizScreen leadId={lead.data.id} answers={lead.data.quizAnswers} flow={flow} />
}

type QuizScreenProps = {
  leadId: string
  answers: Record<string, string>
  flow: NonNullable<ReturnType<typeof getFlow>>
}

/**
 * Figma: pytania `132:2754` (choice), `283:11576` (imię). 1 pytanie = 1 ekran, auto-advance po wyborze,
 * `?step=` wskazuje pytanie (bieżące albo wcześniejsze z ścieżki — wstecz zachowuje odpowiedzi).
 */
function QuizScreen({ leadId, answers, flow }: QuizScreenProps) {
  const search = Route.useSearch()
  const navigate = useNavigate()
  const answer = useAnswer(leadId)
  const [pending, setPending] = useState<string | null>(null)
  const advance = useRef<number | null>(null)

  const state = resume(flow, answers)
  const shownId =
    search.step && (search.step === state.currentId || state.path.includes(search.step))
      ? search.step
      : state.currentId

  useEffect(() => {
    if (shownId === null) navigate({ to: '/konto', replace: true })
  }, [shownId, navigate])
  if (shownId === null) return null

  const question = flow.questions.find((q) => q.id === shownId)
  if (!question) return null
  const shownIndex = state.path.indexOf(shownId)
  const shownState =
    shownIndex === -1 ? state : resume(flow, pick(answers, state.path.slice(0, shownIndex)))
  const { step, total } = progress(flow, shownState)
  const goTo = (step: string) =>
    navigate({ to: '/q/$slug', params: { slug: flow.slug }, search: { step }, replace: false })

  const submit = (value: string) => {
    setPending(value)
    answer.mutate(
      { questionId: question.id, value },
      {
        onSuccess: (lead) => {
          setPending(null)
          if (lead.currentId === null) navigate({ to: '/konto' })
          else goTo(lead.currentId)
        },
        onError: () => setPending(null),
      },
    )
  }

  const errorText =
    answer.error instanceof ApiError
      ? answer.error.code === 'answer_invalid'
        ? 'Sprawdź odpowiedź i spróbuj jeszcze raz.'
        : answer.error.message
      : answer.isError
        ? 'Nie udało się zapisać. Spróbuj jeszcze raz.'
        : null

  return (
    <Shell
      // No arrows in the quiz (Matt, 2026-09-15); browser back still walks the answered steps.
      nav={{}}
      footer={<AskFooter />}
      aside={<QuestionIllustration questionId={question.id} />}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-lg pt-base">
        <div className="flex flex-col gap-base">
          <Progress step={step} total={total} />
          <h1 className="text-h2">{question.title}</h1>
        </div>
        <div className="flex min-h-0 flex-1 flex-col desktop:hidden">
          <QuestionIllustration questionId={question.id} />
        </div>
        {questionKind(question) === 'text' ? (
          <NameForm
            key={question.id}
            initial={answers[question.id] ?? ''}
            placeholder={question.placeholder ?? ''}
            maxLength={question.maxLength ?? 40}
            minLength={question.minLength ?? 2}
            pending={answer.isPending}
            onSubmit={submit}
          />
        ) : (
          <fieldset
            className="flex flex-col gap-base"
            key={question.id}
            disabled={answer.isPending}
          >
            <legend className="sr-only">{question.title}</legend>
            {question.options.map((o) => (
              <Option
                key={o.value}
                name={question.id}
                value={o.value}
                label={o.label}
                {...(o.emoji ? { emoji: o.emoji } : {})}
                checked={(pending ?? answers[question.id]) === o.value}
                onChange={(value) => {
                  // One pending auto-advance at a time: a second tap inside the 150 ms window
                  // replaces the first, so two answers never race (and never double-navigate).
                  if (advance.current !== null) window.clearTimeout(advance.current)
                  advance.current = answer.isPending
                    ? null
                    : window.setTimeout(() => {
                        advance.current = null
                        submit(value)
                      }, 150)
                  setPending(value)
                }}
              />
            ))}
          </fieldset>
        )}
        {errorText ? (
          <p className="text-label text-error" role="alert">
            {errorText}
          </p>
        ) : null}
      </div>
    </Shell>
  )
}

type NameFormProps = {
  initial: string
  placeholder: string
  minLength: number
  maxLength: number
  pending: boolean
  onSubmit: (value: string) => void
}

function NameForm({
  initial,
  placeholder,
  minLength,
  maxLength,
  pending,
  onSubmit,
}: NameFormProps) {
  const [value, setValue] = useState(initial)
  // The length error waits for blur: shown from the first letter it read as a mistake.
  const [touched, setTouched] = useState(false)
  const trimmed = value.trim()
  const valid = trimmed.length >= minLength && trimmed.length <= maxLength
  return (
    <form
      className="flex flex-col gap-base"
      onSubmit={(e) => {
        e.preventDefault()
        if (valid) onSubmit(trimmed)
      }}
    >
      <Input
        label="Twoje imię"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => setTouched(true)}
        autoComplete="given-name"
        enterKeyHint="next"
        maxLength={maxLength}
        {...(touched && value && !valid
          ? { error: `Imię ma mieć ${minLength}–${maxLength} znaki` }
          : {})}
      />
      <Button type="submit" disabled={!valid} loading={pending}>
        Dalej →
      </Button>
    </form>
  )
}

function pick(obj: Record<string, string>, keys: readonly string[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const k of keys) {
    const v = obj[k]
    if (v !== undefined) out[k] = v
  }
  return out
}
